/**
 * File + source bar for the transform-aware mapping phase: file summary, a sheet
 * picker (shown only for multi-sheet workbooks — column-mapping-ui spec, "Header
 * auto-detected and conditional sheet picker"), and an auto-detected header note.
 *
 * Thin feature-specific wrapper around `@ooh/ui`'s generic `SourceFileSummaryBar`
 * (file/sheet summary is not media-import-specific — the availability-import
 * feature needs the identical shell with its own note content, and the two
 * features may not import from each other, per this feature's own spec's
 * cross-feature import ban). Public API kept unchanged so existing call sites
 * are unaffected by the extraction.
 */
import {
  SourceFileSummaryBar,
  type SourceFileSummaryBarLabels,
} from '@ooh/ui/components/source-file-summary-bar';
import { InfoHint } from '@ooh/ui/components/info-hint';

export interface MappingTransformsSourceBarLabels extends SourceFileSummaryBarLabels {
  /** Fully formatted, e.g. "Nagłówek: wiersz 1 · pominięto puste kolumny" */
  headerAutoNote: string;
  /** aria-label for the header-detection info affordance */
  headerAutoInfoLabel: string;
  headerAutoInfoText: string;
}

export interface MappingTransformsSourceBarProps {
  fileName: string;
  /** File size in KB. Omit when unknown — the size segment is dropped from the summary. */
  fileSizeKb?: number;
  estimatedRows: number;
  /** All sheet names in the workbook. The picker only renders when length > 1. */
  sheets: string[];
  selectedSheet: string;
  onSheetChange: (sheet: string) => void;
  labels: MappingTransformsSourceBarLabels;
}

export function MappingTransformsSourceBar({
  fileName,
  fileSizeKb,
  estimatedRows,
  sheets,
  selectedSheet,
  onSheetChange,
  labels,
}: MappingTransformsSourceBarProps) {
  return (
    <SourceFileSummaryBar
      fileName={fileName}
      fileSizeKb={fileSizeKb}
      estimatedRows={estimatedRows}
      sheets={sheets}
      selectedSheet={selectedSheet}
      onSheetChange={onSheetChange}
      labels={labels}
      testId="mapping-transforms-source-bar"
      noteSlot={
        <div className="flex items-center gap-1.5" data-testid="header-row-auto-note">
          <span className="text-xs text-muted-foreground">{labels.headerAutoNote}</span>
          <InfoHint label={labels.headerAutoInfoLabel}>{labels.headerAutoInfoText}</InfoHint>
        </div>
      }
    />
  );
}
